import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot,
} from 'recharts';

function formatTime(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `0:${s.toString().padStart(2, '0')}`;
}

const CustomTooltip = ({ active, payload, label, baseline }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  const dropPct = baseline > 0 ? Math.round((val - baseline) / baseline * 100) : 0;
  return (
    <div style={{
      background: '#fff', border: '1px solid #E4E4E7',
      borderRadius: 8, padding: '10px 14px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    }}>
      <p style={{ color: '#9CA3AF', fontFamily: 'var(--font-mono)', fontSize: 11, marginBottom: 4 }}>
        t = {formatTime(label)}
      </p>
      <p style={{ color: '#09090B', fontSize: 13, fontWeight: 600, margin: 0 }}>
        {val?.toFixed(1)} activation
      </p>
      {dropPct < -10 && (
        <p style={{ color: '#DC2626', fontSize: 11, margin: '3px 0 0' }}>
          {dropPct}% below baseline
        </p>
      )}
    </div>
  );
};

export default function EngagementCurve({ insights, currentTime, onTimeChange, videoDuration }) {
  const { attention, baseline_activation, peak_second, drops, avg_activation, n_timesteps } = insights;

  // Normalize sample indices → real video seconds when we have actual video duration.
  // n_timesteps == attention.length in practice; if timeline was stored as raw sample
  // indices the x-axis would show ~3:39 for a 1:39 video.
  const scale = useMemo(() => {
    const len = attention?.length || 1;
    if (videoDuration && len > videoDuration * 1.1) {
      return videoDuration / Math.max(1, len - 1);
    }
    return 1;
  }, [attention?.length, videoDuration]);

  const data = useMemo(() =>
    (attention || []).map((val, i) => ({
      second: Math.round(i * scale * 10) / 10,
      activation: Math.round(val * 10) / 10,
    })),
    [attention, scale]
  );

  const peakSecondScaled  = Math.round((peak_second ?? 0) * scale * 10) / 10;
  const dropsScaled = useMemo(() =>
    (drops || []).map(d => ({
      ...d,
      start_second: Math.round(d.start_second * scale * 10) / 10,
      end_second:   Math.round(d.end_second   * scale * 10) / 10,
    })),
    [drops, scale]
  );

  const handleClick = (e) => {
    if (e?.activeLabel !== undefined && onTimeChange) onTimeChange(e.activeLabel);
  };

  if (!attention?.length) return null;

  const domain = [
    Math.max(0, Math.min(...attention) - 5),
    Math.min(100, Math.max(...attention) + 5),
  ];

  return (
    <div>
      {/* Stat row */}
      <div style={{ display: 'flex', gap: 32, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="BASELINE (0–3s)" value={baseline_activation?.toFixed(1)} unit="act" />
        <Stat label="AVERAGE"         value={avg_activation?.toFixed(1)}       unit="act" />
        <Stat label="PEAK"            value={formatTime(peakSecondScaled)} />
        <Stat
          label="DROPS"
          value={drops?.length ?? 0}
          valueColor={drops?.length > 0 ? '#DC2626' : '#059669'}
        />
        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: '#9CA3AF', letterSpacing: '0.08em' }}>
            {n_timesteps} TIMESTEPS · TRIBE V2 fMRI
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 160, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} onClick={handleClick} style={{ cursor: 'pointer' }}>
            <defs>
              <linearGradient id="actGradLight" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#18181B" stopOpacity={0.06} />
                <stop offset="95%" stopColor="#18181B" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="second"
              tick={{ fill: '#9CA3AF', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              tickLine={false} axisLine={false}
              tickFormatter={formatTime}
              interval={Math.max(1, Math.floor(data.length / 8))}
            />
            <YAxis domain={domain} hide />
            <Tooltip content={<CustomTooltip baseline={baseline_activation} />} />

            {/* Baseline */}
            <ReferenceLine
              y={baseline_activation}
              stroke="#D1D5DB" strokeDasharray="4 4" strokeWidth={1}
            />

            {/* Drop zone markers */}
            {dropsScaled.map((d, i) => (
              <ReferenceLine
                key={`drop-${i}`}
                x={d.start_second}
                stroke="#DC2626" strokeOpacity={0.35}
                strokeWidth={1} strokeDasharray="3 3"
              />
            ))}

            {/* Peak */}
            <ReferenceDot
              x={peakSecondScaled}
              y={attention?.[peak_second]}
              r={4} fill="#059669" stroke="#fff" strokeWidth={1.5}
            />

            {/* Time cursor */}
            {currentTime !== undefined && (
              <ReferenceLine
                x={Math.round(currentTime)}
                stroke="#D97706" strokeWidth={1.5} strokeOpacity={0.8}
              />
            )}

            <Area
              type="monotone"
              dataKey="activation"
              stroke="#18181B" strokeWidth={2}
              fill="url(#actGradLight)"
              dot={false}
              activeDot={{ r: 4, fill: '#18181B', stroke: '#fff', strokeWidth: 1.5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
        <LegendItem color="#18181B"        label="Neural activation (mean |preds|)" />
        <LegendItem color="#D1D5DB" dashed label="Baseline (0–3s avg)" />
        <LegendItem color="#059669" dot    label="Peak second" />
        {drops?.length > 0 && (
          <LegendItem color="#DC2626" dashed label="Drop zones (>20% below baseline)" />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, unit, valueColor }) {
  return (
    <div>
      <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: '#9CA3AF', letterSpacing: '0.08em', marginBottom: 2 }}>
        {label}
      </p>
      <p style={{ fontSize: 20, fontWeight: 700, color: valueColor || '#09090B', margin: 0, lineHeight: 1 }}>
        {value}
        {unit && <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginLeft: 3 }}>{unit}</span>}
      </p>
    </div>
  );
}

function LegendItem({ color, label, dashed, dot }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {dot ? (
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      ) : (
        <div style={{
          width: 20, height: 2, flexShrink: 0,
          background: dashed ? 'none' : color,
          borderTop: dashed ? `2px dashed ${color}` : 'none',
        }} />
      )}
      <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'var(--font-mono)' }}>{label}</span>
    </div>
  );
}
